import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { HdIcon, HdIconButton, HdRbacButton, HdTooltip } from '../../../components';
import { numFormatter } from '../../../core/utils/number';
import { Activation } from '../models/activation';
import {
  SyncFailureMetaData,
  SyncFailureType, SyncFailureTypeEnum,
} from '../ActivationSyncHistory/models/sync-failure';
import { SyncHistoryItem } from '../ActivationSyncHistory/models/sync-history-item';
import styles from './styles.module.scss';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { RbacPermissions } from '../../../core/interfaces/user';
import { RecordsFailureContext } from '../RecordsFailure/recordsFailureContext';
import RecordsFailure from '../RecordsFailure';
import {
  INITIAL_FAILED_RECORDS_STATE,
  recordsFailureReducer,
  RecordsStatus
} from '../RecordsFailure/recordsFailureReducer';
import ActivationsAPI from '../api/ActivationsAPI';
import {
  TRACKER_ACTIVATION_SYNC_HISTORY_FAILURE_DETAILS_FETCH_FAILED,
  TRACKER_ACTIVATION_SYNC_HISTORY_FAILURE_DETAILS_FETCH_SUCCESS,
  TRACKER_ACTIVATION_SYNC_HISTORY_QUERY_FAILED_RECORDS_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_RETRY_FAILED_RECORDS_CLICK
} from '../tracking';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import HdLink from '../../../link/components/link/HdLink';
import HdTimeAgo from '../../../date-time/components/time-ago/HdTimeAgo';
import QueryFailedRecordsDialog from '../QueryFailedRecordsDialog';
import { throwError } from 'rxjs';
import { getErrorMessage } from 'codelyzer/templateAccessibilityElementsContentRule';
import { getErrorMessageFromObj } from '../../../core/utils/request';

interface FailedRecordsCardsProps {
  activation: Activation;
  failureType: SyncFailureType;
  failureMetaData: SyncFailureMetaData;
  lastRetriedSyncRun?: SyncHistoryItem;
}

function FailedRecordsCard({
 activation,
 failureType,
 failureMetaData,
 lastRetriedSyncRun
}: FailedRecordsCardsProps) {

  const analyticsTracker = useAnalyticsTracker();
  const [showFailedRecords, setShowFailedRecords] = useState(false);
  const lastRetriedRunDisplayProps = useMemo(() => {
    if (!lastRetriedSyncRun) {
      return null;
    }

    return ({
      'Run Status': lastRetriedSyncRun.status.name,
      'Query Result Records': lastRetriedSyncRun.getQueryResultRecords(),
      'Records Processed': lastRetriedSyncRun.recordsProcessed,
      'Records Succeeded': lastRetriedSyncRun.getRecordsSuccess(),
      'Records Failed': lastRetriedSyncRun.getRecordsFailed()
    });
  }, [lastRetriedSyncRun]);

  const [showLastRunQueryDialog, setLastRunQueryDialog] = useState(false);

  const [failedRecordsState, dispatch] = useReducer(
    recordsFailureReducer,
    INITIAL_FAILED_RECORDS_STATE
  );

  const retryFailedRecords = (e) => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_RETRY_FAILED_RECORDS_CLICK,
      properties: {
        activationId: activation.id,
        failureType: failureType.value
      }
    });

    ActivationsAPI.retryFailedActivationRun(activation.id);
  };

  const queryFailedRecords = () => {

    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_QUERY_FAILED_RECORDS_CLICK,
      properties: {
        activationId: activation.id,
        failureType: failureType.value
      }
    });

    setLastRunQueryDialog(true);
  };

  const getFailedRecordsObservable = () => {
    dispatch({ type: RecordsStatus.GET_ACTIVATION_RECORDS_FAILURE });

    ActivationsAPI.getActivationRunErrorBasedOnType(activation.id, failureType.value)
      .then((res) => {
        if (!res.data?.errors) {

          return dispatch({
            type: RecordsStatus.SUCCESS_ACTIVATION_RECORDS_FAILURE,
            payload: {
              failureDetails: []
            }
          });
        }

        analyticsTracker.eventTrack({
          action: TRACKER_ACTIVATION_SYNC_HISTORY_FAILURE_DETAILS_FETCH_SUCCESS,
          properties: {
            activationSeqId: activation.seqId,
            failureType: failureType.value,
            failureCount: res.data.total_failures
          }
        });

        dispatch({
          type: RecordsStatus.SUCCESS_ACTIVATION_RECORDS_FAILURE,
          payload: {
            failureDetails: res.data.errors,
            failureCount: res.data.total_failures || 0
          }
        });
      }).catch((err) => {
      analyticsTracker.eventTrack({
        action: TRACKER_ACTIVATION_SYNC_HISTORY_FAILURE_DETAILS_FETCH_FAILED,
        properties: {
          activationSeqId: activation.seqId,
          failureType: failureType.value,
          errorMessage: err.message
        }
      });

      dispatch({
        type: RecordsStatus.ERROR_ACTIVATION_RECORDS_FAILURE,
        payload: {
          error: getErrorMessageFromObj(err)
        }});
    })
  };

  const getFailedRecordsDownloadDetailsObservable = () => {
    return ActivationsAPI.getActivationRunErrorReportOnType(activation.id, failureType.value);
  };

  const getFailureScope = failureType === SyncFailureTypeEnum.PERMANENT ? 'Failed' : 'Error';

  const dataIdGenerator = getDataIdGenerator('activation-summary');

  return (
    <div className={styles.failedRecordsCard}>
      <div className='center-flex-row justify-between px-2'>
        <div className='center-flex-row'>
          <HdIcon
            className={`text-${failureType.alertType} mr-3`}
            name='error-filled'>
          </HdIcon>

          <div className='flex-col'>
            <div className='d-flex'>
              <HdTooltip

                title={failureType.description}
                disabled={failureMetaData.recordsFailedCount === 0}
              >
                <div className='text-medium'>
                  {failureType.name}
                </div>
              </HdTooltip>

              <span className='text-secondary mx-2'>&bull;</span>

              <span className={`text-${failureType.alertType}`}>
                {numFormatter(failureMetaData.recordsFailedCount)} Records
              </span>
            </div>

            {failureType.canRetry && lastRetriedSyncRun ? (
              <div className='center-flex-row mt-1'>
              <span className='text-secondary mr-1'>
                Last Retry
              </span>

                <HdTooltip
                  title={
                    <div className='config-popup'>
                      <div className='config-container'>
                        {Object.entries(lastRetriedRunDisplayProps).map(([key, value], index) => (
                          <div className='config' key={index}>
                            <span className='text-medium'>{key}</span>: {value}
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                  mode='surface'
                  customStyle={{ maxWidth: 'initial' }}
                  placement='right-start'
                  disableInteractive={false}
                >
                  <div>
                    <HdLink dataId={dataIdGenerator('lastRetriedSyncRun')}>
                      <HdTimeAgo timestamp={lastRetriedSyncRun.updatedTs} />
                    </HdLink>
                    <HdIcon name='popup' className='ml-1 text-primary' />
                  </div>
                </HdTooltip>
              </div>
            ) : null}
          </div>
        </div>

        <div className='center-flex-row mr-1'>
          {failureType.canRetry ? (
            <HdRbacButton
              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
              type='button'
              size='sm'
              variation='outline'
              icon='restart'
              onClick={retryFailedRecords}
              dataId={dataIdGenerator('retry')}
            >
              Retry
            </HdRbacButton>
          ) : null}

          <HdRbacButton
            rbacPermission={RbacPermissions.ACTIVATION_EDIT}
            type='button'
            size='sm'
            variation='outline'
            icon='custom-sql'
            onClick={queryFailedRecords}
            dataId={dataIdGenerator('getQuery')}
            className={`ml-3 ${styles.queryFailedRecordsBtn}`}
          >
            Query {failureType.name}
          </HdRbacButton>

          <HdIconButton
            horizontalEdge='both'
            verticalEdge='both'
            className='ml-3 p-1'
            onClick={() => setShowFailedRecords(!showFailedRecords)}
            dataId={dataIdGenerator('showFailedRecords')}
          >
            <HdIcon name={showFailedRecords ? 'up-arrow' : 'dropdown-arrow'} />
          </HdIconButton>
        </div>
      </div>

      {showFailedRecords ? (
        <div className='mt-3'>
          <RecordsFailureContext.Provider value={failedRecordsState}>
            <RecordsFailure
              getRecordFailures={getFailedRecordsObservable}
              failureScope={getFailureScope}
              queryRecordsClick={queryFailedRecords}
              downloadFailureDetails={getFailedRecordsDownloadDetailsObservable}
            />
          </RecordsFailureContext.Provider>
        </div>
      ) : null}

      <QueryFailedRecordsDialog
        open={showLastRunQueryDialog}
        onClose={setLastRunQueryDialog}
        activation={activation}
        query={failureMetaData.warehouseErrorQuery || ''}
        failureType={failureType}
        failureScope={getFailureScope}
      />
    </div>
  );
}

export default FailedRecordsCard;
